// A brand's library list as pages (S1: `AssetPage {items, next}` with an opaque cursor), for every
// screen that lists library files: the library page and the Sounds page. Orval writes no infinite
// query, so this one is built from the generated `listAssets` and its key.
import {
  keepPreviousData,
  replaceEqualDeep,
  useInfiniteQuery,
  type InfiniteData,
} from "@tanstack/react-query";

import { getListAssetsQueryKey, listAssets } from "../../api/generated/endpoints/assets/assets";
import type {
  AssetKind,
  AssetOrder,
  AssetPage,
  AssetSource,
  ListAssetsParams,
} from "../../api/generated/model";
import { freshUrls, keptUrl, signedExpiry, soonestExpiry } from "../../api/signed-urls";

export interface LibraryFilter {
  kind?: AssetKind | undefined;
  folderId?: string | undefined;
  /** With `folderId`: the folders under it too, live's "Show nested" (spec A3b). */
  recursive?: boolean | undefined;
  trash: boolean;
  /** Part of the name (#308); live's library search. */
  search?: string | undefined;
  /**
   * Where the files came from, any of these: live's Creative (`generation`) and Manual Uploads
   * (`upload`); the editor's Manual is an upload or a URL import (#244).
   */
  source?: readonly AssetSource[] | undefined;
  /** Who added them, any of these: live's creator chips (#388). */
  createdBy?: readonly string[] | undefined;
  /** Saved from jobs that showed this product: live's Product filter (#388). */
  productId?: string | undefined;
  /** The job type the files were saved from (#308): the Sounds page's chips. */
  generationType?: string | undefined;
  order?: AssetOrder | undefined;
}

/** Live's sort words (`defaultOrderOptions`, capture gaps/28): newest first by default. */
export const LIBRARY_ORDERS: readonly { value: AssetOrder; label: string }[] = [
  { value: "newest", label: "Recently Added" },
  { value: "oldest", label: "Oldest First" },
];

/** The route's limit on a search (#308). */
export const LIBRARY_SEARCH_MAX = 255;

const PAGE_SIZE = 50;
/** While a listed video's 540p copy is being made, a list that watches copies is read again this
 *  often (the editor's bin, spec editor/proxy-playback.md); the editor's project reads wait the same. */
export const PENDING_COPY_POLL_MS = 30_000;
/** The pages are read again on focus only once this old (or once their URLs are due): an upload,
 *  a move or a delete of ours refreshes them at once (`refreshLibraryLists`), and a focus after
 *  10 s re-read every loaded page (#794). */
const PAGES_STALE_MS = 5 * 60_000;

type LibraryPages = InfiniteData<AssetPage, string | undefined>;

/**
 * The pages a read brings, with each file's URLs kept as shown while they are good (`keptUrl`):
 * the grid fetches no picture it already has (#794). The rest is TanStack's own sharing, so a
 * file that did not change stays the same object.
 */
export function keptPages(shown: LibraryPages | undefined, read: LibraryPages, now: number = Date.now()): LibraryPages {
  if (shown === undefined) return read;
  const before = new Map(shown.pages.flatMap((page) => page.items).map((asset) => [asset.id, asset.urls]));
  const pages = read.pages.map((page) => ({
    ...page,
    items: page.items.map((asset) => {
      const urls = before.get(asset.id);
      if (urls === undefined) return asset;
      return {
        ...asset,
        urls: {
          original: keptUrl(urls.original, asset.urls.original, now),
          thumb: keptUrl(urls.thumb, asset.urls.thumb, now),
          poster: keptUrl(urls.poster, asset.urls.poster, now),
          preview: keptUrl(urls.preview ?? null, asset.urls.preview ?? null, now),
          // A video's 540p copy (spec editor/proxy-playback.md), where the read names one.
          ...(asset.urls.proxy === undefined ? {} : { proxy: keptUrl(urls.proxy ?? null, asset.urls.proxy, now) }),
        },
      };
    }),
  }));
  return replaceEqualDeep(shown, { ...read, pages });
}

/** When the soonest URL the pages show stops working (the list names no expiry of its own). */
export function pagesExpiry(data: LibraryPages): string | null {
  return soonestExpiry(
    data.pages.flatMap((page) =>
      page.items.flatMap((asset) =>
        [asset.urls.original, asset.urls.thumb, asset.urls.poster, asset.urls.preview ?? null, asset.urls.proxy ?? null].map(
          signedExpiry,
        ),
      ),
    ),
  );
}

/** The filter as the API's params; an absent filter is left out, never sent as "null". */
export function libraryParams(filter: LibraryFilter): ListAssetsParams {
  return {
    ...(filter.kind === undefined ? {} : { kind: filter.kind }),
    ...(filter.folderId === undefined ? {} : { folder_id: filter.folderId }),
    // The API refuses `recursive` without a folder, so it goes only with one.
    ...(filter.recursive === true && filter.folderId !== undefined ? { recursive: true } : {}),
    trash: filter.trash,
    ...(filter.search === undefined || filter.search === "" ? {} : { search: filter.search }),
    ...(filter.source === undefined || filter.source.length === 0
      ? {}
      : { source: [...filter.source] }),
    ...(filter.createdBy === undefined || filter.createdBy.length === 0
      ? {}
      : { created_by: [...filter.createdBy] }),
    ...(filter.productId === undefined ? {} : { product_id: filter.productId }),
    ...(filter.generationType === undefined ? {} : { generation_type: filter.generationType }),
    ...(filter.order === undefined || filter.order === "newest" ? {} : { order: filter.order }),
  };
}

/** How a list pages: its page size, and whether a new filter keeps the last pages until it lands. */
export interface LibraryPaging {
  /** Live's page where a screen has its own (the editor's 48 and 24); 50 otherwise. */
  size?: number;
  /** A search typed into the list keeps what it shows until its answer comes (the editor's bin). */
  keepPrevious?: boolean;
  /** Read again every `PENDING_COPY_POLL_MS` while a listed video's 540p copy is being made, so its
   *  tile's "Preparing preview" goes once it is (the editor's bin, spec editor/proxy-playback.md). */
  watchCopies?: boolean;
}

/** Whether the pages list a video whose 540p copy is being made. */
function copyPending(data: LibraryPages | undefined): boolean {
  return (
    data?.pages.some((page) => page.items.some((asset) => asset.kind === "video" && asset.proxy_status === "pending")) ??
    false
  );
}

export function useLibraryPages(
  teamId: string,
  brandId: string,
  filter: LibraryFilter,
  /** Off while the screen shows another list (Add Actors' other sources). */
  enabled = true,
  paging: LibraryPaging = {},
) {
  const params = libraryParams(filter);
  const size = paging.size ?? PAGE_SIZE;
  const fresh = freshUrls(pagesExpiry);
  return useInfiniteQuery({
    enabled,
    ...(paging.keepPrevious === true ? { placeholderData: keepPreviousData } : {}),
    staleTime: PAGES_STALE_MS,
    // Read again shortly before the URLs expire, and on focus once they are due; while a copy is
    // being made, every `PENDING_COPY_POLL_MS` for a list that watches copies, its pictures kept.
    refetchInterval: (query) =>
      paging.watchCopies === true && copyPending(query.state.data)
        ? PENDING_COPY_POLL_MS
        : fresh.refetchInterval(query),
    refetchOnWindowFocus: fresh.refetchOnWindowFocus,
    // TanStack's own data for this key, typed by the query function below.
    structuralSharing: (shown, read) => keptPages(shown as LibraryPages | undefined, read as LibraryPages),
    // "pages" keeps these pages apart from a plain `useListAssets` of the same params.
    queryKey: [...getListAssetsQueryKey(teamId, brandId, params), "pages"],
    queryFn: ({ pageParam, signal }) =>
      listAssets(
        teamId,
        brandId,
        { ...params, limit: size, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });
}
